import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, CurrencyField, FormGrid, NumberField } from "@atlas/ui/web";
import type { RegrasRefinanciamento } from "@atlas/sdk";
import { atlas } from "../lib/sdk";
import { mensagemDeErro } from "../lib/erro-validacao";
import { fmtDataHora } from "../lib/refinanciamento";

/** Regras de refinanciamento com o proprio credor, por convenio. Salva a parte
 *  da configuracao geral: e' outro recurso na API, com auditoria propria. */
export function RegrasRefinanciamentoConvenio({ convenioId }: { convenioId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "convenios", convenioId, "refinanciamento"],
    queryFn: () => atlas.refinanciamento.regras(convenioId),
  });
  const [form, setForm] = useState<RegrasRefinanciamento | null>(null);
  const [ok, setOk] = useState(false);
  useEffect(() => {
    if (q.data && !form) {
      const { configurada: _c, atualizadoPor: _p, atualizadoEm: _e, ...regras } = q.data.regras;
      setForm(regras);
    }
  }, [q.data, form]);

  const save = useMutation({
    mutationFn: () => atlas.refinanciamento.salvarRegras(convenioId, form!),
    onSuccess: (r) => {
      qc.setQueryData(["admin", "convenios", convenioId, "refinanciamento"], r);
      setOk(true);
    },
  });

  const meta = q.data?.regras;
  const set = <K extends keyof RegrasRefinanciamento>(k: K, v: RegrasRefinanciamento[K]) => {
    setOk(false);
    setForm((f) => (f ? { ...f, [k]: v } : f));
  };

  return (
    <section style={{ display: "grid", gap: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }} aria-label="Refinanciamento">
      <div>
        <h4 style={{ margin: 0 }}>Refinanciamento com a própria consignatária</h4>
        <p style={{ color: "var(--text-muted)", margin: "4px 0 0", fontSize: 13 }}>
          {meta?.configurada
            ? `Última alteração por ${meta.atualizadoPor ?? "—"} em ${fmtDataHora(meta.atualizadoEm)}.`
            : "Sem regra gravada: vale o padrão abaixo até alguém salvar."}
        </p>
      </div>
      {q.error ? <div style={{ color: "var(--danger-500)", fontSize: 13 }}>{mensagemDeErro(q.error, "Não foi possível carregar as regras.")}</div> : null}
      {form ? (
        <>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
            <input type="checkbox" checked={form.permitido} onChange={(e) => set("permitido", e.target.checked)} />
            Permitir refinanciamento neste convênio
          </label>
          <FormGrid cols={3}>
            <NumberField
              label="Parcelas pagas mínimas"
              value={form.minParcelasPagas}
              min={0}
              max={120}
              onChange={(e) => set("minParcelasPagas", Math.max(0, Number(e.target.value) || 0))}
              hint="Antes disso o contrato não pode ser refinanciado"
            />
            <NumberField
              label="Validade da proposta (horas)"
              value={form.validadeHoras}
              min={1}
              max={720}
              onChange={(e) => set("validadeHoras", Math.max(1, Number(e.target.value) || 1))}
              hint="Depois disso a proposta expira"
            />
            <CurrencyField
              label="Troco mínimo"
              value={form.trocoMinimo}
              onValueChange={(v) => set("trocoMinimo", v ?? 0)}
              hint="Zero = qualquer valor"
            />
          </FormGrid>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
            <input type="checkbox" checked={form.permiteTroco} onChange={(e) => set("permiteTroco", e.target.checked)} />
            Permitir troco (valor liberado acima do saldo de quitação)
          </label>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
            <input type="checkbox" checked={form.permiteUnificar} onChange={(e) => set("permiteUnificar", e.target.checked)} />
            Permitir unificar vários contratos da mesma consignatária num só
          </label>
          {save.error ? <div style={{ color: "var(--danger-500)", fontSize: 13 }}>{mensagemDeErro(save.error, "Não foi possível salvar as regras.")}</div> : null}
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Button type="button" variant="ghost" disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Salvando..." : "Salvar regras de refinanciamento"}
            </Button>
            {ok ? <span role="status" style={{ color: "var(--emerald-500)", fontSize: 13 }}>Regras salvas.</span> : null}
          </div>
        </>
      ) : q.isLoading ? (
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>Carregando…</p>
      ) : null}
    </section>
  );
}
